import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { Alert, Button } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';
import { toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import StandardLayout from '@/common/layouts/StandardLayout';
import DataTable from '@/common/tableData/DataTable';
import Paginator from '@/pages/common/paginator';
import DebouncedInput from '@/components/DebounceInput';

import {
  fetchAgentDealUpdateRequestsRest,
  applyAgentDealUpdateRequestRest,
  deleteAgentDealUpdateRequestRest,
  createAgentDealUpdateRequestRest,
  updateAgentDealUpdateRequestRest,
  convertAgentDealUpdateRequestsFilterToApiFormat,
} from '@/services/agents/agents-service';
import {
  AdminAgentDealUpdateRequestsResponseDto,
  AdminAgentDealUpdateRequestDto,
  CreateAgentDealUpdateRequestDto,
  UpdateAgentDealUpdateRequestDto,
} from '@repo/common-types';
import { createColumns } from './agent-deal-update-requests.columns';
import { AgentDealUpdateRequestsFilter, AgentDealUpdateRequestFormModal } from './components';
import { initialState, queryReducer } from './agent-deal-update-requests-query-reducer';
import { RiAddLine, RiFilter3Fill } from 'react-icons/ri';
import { convertSortingToApiFormat } from '@/common/utils/convert-sorting-to-api-format';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';

// Use shared query reducer (mirrors Agents pattern)

const AgentDealUpdateRequestsPage: React.FC = () => {
  const [queryParams, dispatch] = useReducer(queryReducer, initialState);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState<AdminAgentDealUpdateRequestDto | undefined>(undefined);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUpdateId, setSelectedUpdateId] = useState<string | undefined>(undefined);

  const { sorting, ...rest } = queryParams;

  const { data, isLoading, error, refetch } = useQuery<AdminAgentDealUpdateRequestsResponseDto>({
    queryKey: ['agentDealUpdateRequests', queryParams],
    queryFn: async () => {
      return await fetchAgentDealUpdateRequestsRest({
        skip: rest.skip,
        take: rest.take,
        filter: convertAgentDealUpdateRequestsFilterToApiFormat(rest.filter),
        sorts: convertSortingToApiFormat(sorting),
      });
    },
    staleTime: 60000,
  });

  // Check if filters differ from initial state
  const hasActiveFilters = useMemo(() => {
    const currentFilter = queryParams.filter || {};
    const initialFilter = initialState.filter || {};

    return Object.keys(initialFilter).some((key) => {
      const currentValue = currentFilter[key as keyof typeof currentFilter];
      const initialValue = initialFilter[key as keyof typeof initialFilter];
      return currentValue !== initialValue;
    });
  }, [queryParams.filter]);

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      dispatch({ type: 'SET_SORTING', payload: newSorting });
    },
    [sorting],
  );

  const handlePaginationChange = useCallback((skip: number, take: number) => {
    dispatch({ type: 'SET_PAGINATION', payload: { skip, take } });
  }, []);

  const handleSearch = useCallback((value: string | number) => {
    dispatch({ type: 'SET_SEARCH', payload: value.toString() });
  }, []);

  const handleOpenFilterModal = () => setShowFilterModal(true);
  const handleCloseFilterModal = () => setShowFilterModal(false);
  const handleCreateAgentDealUpdateRequest = () => setShowCreateModal(true);
  const handleCloseCreateModal = () => setShowCreateModal(false);

  const handleApply = useCallback(
    async (update: AdminAgentDealUpdateRequestDto) => {
      try {
        await applyAgentDealUpdateRequestRest(update.id);
        await refetch();
        toast.success('Agent deal update request applied.', {
          position: 'top-right',
          autoClose: 4000,
          theme: 'dark',
          transition: Bounce,
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to apply update';
        toast.error(msg, { position: 'top-right', autoClose: 7000, theme: 'dark', transition: Bounce });
      }
    },
    [refetch],
  );

  const handleDelete = (update: AdminAgentDealUpdateRequestDto) => {
    setSelectedUpdateId(update.id);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = useCallback(async () => {
    try {
      if (!selectedUpdateId) throw new Error('No agent deal update request selected');
      await deleteAgentDealUpdateRequestRest(selectedUpdateId);
      await refetch();
      toast.success('Agent deal update request deleted.', {
        position: 'top-right',
        autoClose: 4000,
        theme: 'dark',
        transition: Bounce,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete update';
      toast.error(msg, { position: 'top-right', autoClose: 7000, theme: 'dark', transition: Bounce });
    } finally {
      setShowConfirmModal(false);
      setSelectedUpdateId(undefined);
    }
  }, [refetch, selectedUpdateId]);

  const handleFormSubmit = useCallback(
    async (dto: CreateAgentDealUpdateRequestDto | UpdateAgentDealUpdateRequestDto) => {
      try {
        if (selectedUpdate) {
          // Update existing agent deal update request
          await updateAgentDealUpdateRequestRest(selectedUpdate.id, dto as UpdateAgentDealUpdateRequestDto);
          toast.success('Agent deal update request updated.', {
            position: 'top-right',
            autoClose: 4000,
            theme: 'dark',
            transition: Bounce,
          });
        } else {
          // Create new agent deal update request
          await createAgentDealUpdateRequestRest(dto as CreateAgentDealUpdateRequestDto);
          toast.success('Agent deal update request created.', {
            position: 'top-right',
            autoClose: 4000,
            theme: 'dark',
            transition: Bounce,
          });
        }

        await refetch();
        handleCloseCreateModal();
        setSelectedUpdate(undefined);
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : selectedUpdate
              ? 'Failed to update agent deal update request'
              : 'Failed to create agent deal update request';
        toast.error(msg, { position: 'top-right', autoClose: 7000, theme: 'dark', transition: Bounce });
      }
    },
    [selectedUpdate, refetch],
  );

  const handleEditUpdate = (update: AdminAgentDealUpdateRequestDto) => {
    setSelectedUpdate(update);
    setShowCreateModal(true);
  };

  const columns = useMemo(
    () => createColumns(handleApply, handleDelete, handleEditUpdate),
    [handleApply, handleDelete, handleEditUpdate],
  );

  return (
    <StandardLayout
      pageTitle="Agent Deal Update Requests"
      headerButton={
        <Button color="primary" onClick={handleCreateAgentDealUpdateRequest}>
          <RiAddLine />
          Add Agent Deal Update Request
        </Button>
      }
    >
      {error ? (
        <Alert color="danger">Error when fetching agent deal update requests</Alert>
      ) : (
        <>
          <div className="w-100 d-flex justify-content-between align-items-center mb-4 gap-2">
            <div className="d-flex align-items-center justify-content-start gap-2">
              <DebouncedInput
                className="form-control w-auto"
                placeholder="Search"
                isLoading={false}
                onChange={handleSearch}
              />
              <Button type="button" outline color="dark" onClick={handleOpenFilterModal}>
                <span className="d-flex align-items-center gap-2">
                  <RiFilter3Fill size={20} />
                  Filter
                </span>
              </Button>
              {hasActiveFilters && (
                <Button type="button" outline color="dark" onClick={() => dispatch({ type: 'RESET_FILTERS' })}>
                  Clear Filters
                </Button>
              )}
            </div>
          </div>

          <DataTable
            data={data?.items || []}
            columns={columns as any}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            enableSorting={true}
          />

          <Paginator
            take={queryParams.take || 25}
            skip={queryParams.skip || 0}
            showing={(data?.items || []).length}
            totalCount={data?.totalCount || 0}
            setTake={(take) => handlePaginationChange(queryParams.skip || 0, take)}
            setSkip={(skip) => handlePaginationChange(skip, queryParams.take || 25)}
          />
        </>
      )}
      {showFilterModal && (
        <AgentDealUpdateRequestsFilter
          onClose={handleCloseFilterModal}
          onApplyFilters={(filters) => {
            dispatch({ type: 'SET_FILTERS', payload: filters });
            // Let the modal close itself after applying filters
            setShowFilterModal(false);
          }}
          currentFilters={queryParams.filter}
        />
      )}
      {showCreateModal && (
        <AgentDealUpdateRequestFormModal
          onClose={() => {
            setSelectedUpdate(undefined);
            handleCloseCreateModal();
          }}
          onSubmit={handleFormSubmit}
          agentDealUpdateRequest={selectedUpdate}
        />
      )}
      {showConfirmModal && (
        <ConfirmModal
          onClose={() => {
            setShowConfirmModal(false);
            setSelectedUpdateId(undefined);
          }}
          onConfirm={handleConfirmDelete}
          title="Confirm"
          message="Are you sure you want to delete this agent deal update request?"
        />
      )}
    </StandardLayout>
  );
};

export default AgentDealUpdateRequestsPage;
